import Consts from '../utils/constants';
import Complex from './complex';
import linalg from './linalg';

export default {
    // TODO: Sort this out into multiple different formula.js files, and move some of these into utils.js!

    // This function gets the next highest subdecade for log plots.
    // Examples:
    //      2.3 will return 3
    //      10 will return 20
    //      2452 will return 3000
    //      3000 will return 4000
    getSubDecade(value) {
        let numberString = value.toExponential(),
            eIndex = numberString.indexOf('e'),
            mantissaString = numberString.slice(0, eIndex),
            mantissa = Number(mantissaString),
            roundedUpMantissa = Math.ceil(mantissa);

        //in case the mantissa ended up being an integer, we still need the next highest number
        if (roundedUpMantissa === mantissa) roundedUpMantissa++;

        let exponentString = numberString.slice(eIndex + 1, numberString.length),
            subDecadeString = roundedUpMantissa + 'e' + exponentString,
            subDecade = Number(subDecadeString);

        return subDecade;
    },

    // This function gets the first power of 10 that is larger than the value passed in.
    // 2.3 will return 10
    // 10 will return 100
    // 2452 will return 10000
    getDecade(value) {
        let numberString = value.toExponential(),
            eIndex = numberString.indexOf('e'),
            exponentString = numberString.slice(eIndex + 1, numberString.length),
            exponent = Number(exponentString) + 1,
            decade = Math.pow(10, exponent);

        return decade;
    },

    // Calculates the parallel value.
    // Dynamtic arguments.
    parallel() {
        let summed = 0;

        for (let i = 0; i < arguments.length; i++) summed = 1 / arguments[i] + summed;

        return 1 / summed;
    },

    //#region noiseCalculations
    calculateDegredation(frequency, hdCorner, hdSlope) {
        return Math.pow(frequency / hdCorner, hdSlope / 20);
    },

    calculateHdCurveUnfiltered(hdDegredation, hdFlat) {
        return hdFlat - 20 * Math.log10(1 / (1 + hdDegredation));
    },

    calculateHD2Curve(frequency, hdCurveUnfiltered, threeDbFrequency) {
        return (
            20 *
            (Math.log10(Math.pow(10, hdCurveUnfiltered / 20)) /
                Math.sqrt(1 + (2 * frequency) / (threeDbFrequency * threeDbFrequency)))
        );
    },

    calculateHD3Curve(frequency, hdCurveUnfiltered, threeDbFrequency) {
        return (
            20 *
            (Math.log10(Math.pow(10, hdCurveUnfiltered / 20)) /
                Math.sqrt(1 + (3 * frequency) / (threeDbFrequency * threeDbFrequency)))
        );
    },

    calculateEnob(sinad) {
        return (sinad - 1.76) / 6.02;
    },

    calculateSinad(snr, thd) {
        return -10 * Math.log10(Math.pow(10, -snr / 10) + Math.pow(10, thd / 10));
    },

    calculateSnr(signal, noise) {
        return 20 * Math.log10(signal / noise);
    },

    calculateThd(frequency, rext, adc) {
        let thdBrickwall, thdRfiltDegredationModel;

        thdBrickwall = adc.thdSlope * Math.log10(frequency / adc.thdCorner) - adc.thdBaseline;

        thdRfiltDegredationModel = 20 * Math.log10(2 * Math.PI * frequency * rext * adc.Cerror);

        return (
            10 *
            Math.log10(
                Math.pow(10, -adc.thdBaseline / 10) +
                    Math.pow(10, thdBrickwall / 10) +
                    Math.pow(10, thdRfiltDegredationModel / 10),
            )
        );
    },

    calculateAdjustedThd(frequency, rext, adc, adjustment) {
        let thdBrickwall, thdRfiltDegredationModel;

        thdBrickwall = adc.thdSlope * Math.log10(frequency / adc.thdCorner) - adc.thdBaseline;

        thdRfiltDegredationModel = 20 * Math.log10(2 * Math.PI * frequency * rext * adc.Cerror);

        return (
            10 *
            Math.log10(
                Math.pow(10, -adc.thdBaseline / 10) +
                    Math.pow(10, thdBrickwall / 10) +
                    Math.pow(10, thdRfiltDegredationModel / 10) +
                    Math.pow(10, adjustment / 10),
            )
        );
    },
    //#endregion noiseCalculations

    //#region lineDecimation
    decimateArrays(valuesArrays, allowedLength, manditoryIndexes) {
        let decimatedValuesArrays = [],
            len = valuesArrays[0].length,
            uLen = valuesArrays.length;

        if (len <= allowedLength) return valuesArrays;

        let factor = Math.round(len / allowedLength),
            indexes = manditoryIndexes.slice(0); // Need to clone the array because arrays are passed by ref.

        if (factor === 0) factor = 1;

        for (let u = 0; u < uLen; u++) decimatedValuesArrays[u] = [];

        for (let i = 0; i < len; i += factor)
            do {
                if (i > indexes[0]) {
                    let index = indexes.shift();

                    for (let x = 0; x < uLen; x++) {
                        decimatedValuesArrays[x].push(valuesArrays[x][index]);

                        if (!(i > indexes[0])) decimatedValuesArrays[x].push(valuesArrays[x][i]);
                    }
                } else if (i === indexes[0]) {
                    let index = indexes.shift();

                    for (let x = 0; x < uLen; x++) decimatedValuesArrays[x].push(valuesArrays[x][index]);
                } else for (let x = 0; x < uLen; x++) decimatedValuesArrays[x].push(valuesArrays[x][i]);
            } while (i > indexes[0]);

        return decimatedValuesArrays;
    },

    removeConsecutiveValues(values, precision) {
        let decimatedValues = [];

        if (values.length > 2) {
            decimatedValues.push(values[0]);

            for (let i = 1, len = values.length; i < len; i++) {
                let target = Math.round(values[i] * precision) / precision;

                if (
                    target === Math.round(values[i - 1] * precision) / precision &&
                    target === Math.round(values[i + 1] * precision) / precision
                )
                    decimatedValues.push(false);
                else decimatedValues.push(values[i]);
            }
        }

        return decimatedValues;
    },
    //#endregion lineDecimation

    //#region inputSettlingCalculations
    vinFromVout(vout, bias, rg, rf, gain, topology) {
        let result;

        if (topology === Consts.TOPOLOGY.FOLLOWER || (topology === Consts.TOPOLOGY.NON_INVERTING && gain === 1)) {
            result = vout;
        } else if (topology === Consts.TOPOLOGY.NON_INVERTING) {
            result = ((vout - bias) * rg) / (rf + rg) + bias;
        } else if (topology === Consts.TOPOLOGY.INVERTING) {
            result = bias - ((vout - bias) * rg) / rf;
        }

        return result;
    },
    calculateVpoleStart(voutInit, vcapInit, vinMinusInit, rext, rf, ro) {
        let vpoleStart;

        if (rf == 0) {
            vpoleStart = voutInit + ro * ((voutInit - vcapInit) / rext);
        } else {
            vpoleStart = voutInit + ro * ((voutInit - vcapInit) / rext + (voutInit - vinMinusInit) / rf);
        }
        return vpoleStart;
    },
    calculateVpole(vpole, vinPlus, vinMinus, tStep, slewRate, GBWP) {
        let vpoleI, dvGbw, dvSlewRate, dv, newPole;
        // This looks like Forward Euler while we use trapezoidal elsewhere.
        // If we wanted trapezoidal at this level, the update should be
        // tStep * Math.PI * GBWP * ((vinPlus - vinMinus) + vHist where
        // vHist  = tStep * Math.PI * GBWP * (vinPlusPrev - vinMinusPrev) + vpolePrev
        vpoleI = (vinPlus - vinMinus) * 2 * Math.PI * GBWP * tStep + vpole;

        dvGbw = vpoleI - vpole;

        dvSlewRate = slewRate * tStep;

        dv = Math.min(dvGbw, dvSlewRate);

        newPole = dv + vpole;

        return newPole;
    },

    calculateVinMinusPlus(vin, bias, gain, topology) {
        let result;

        if (topology === Consts.TOPOLOGY.FOLLOWER || (topology === Consts.TOPOLOGY.NON_INVERTING && gain === 1)) {
            result = vin;
        } else if (topology === Consts.TOPOLOGY.NON_INVERTING) {
            result = vin;
        } else if (topology === Consts.TOPOLOGY.INVERTING) {
            result = bias;
        }

        return result;
    },
    calculateVinMinus(vout, vin, bias, rg, rf, gain, topology) {
        let result;

        if (topology === Consts.TOPOLOGY.FOLLOWER || (topology === Consts.TOPOLOGY.NON_INVERTING && gain === 1)) {
            result = vout;
        } else if (topology === Consts.TOPOLOGY.NON_INVERTING) {
            result = ((vout - bias) * rg) / (rf + rg) + bias;
            // result = (bias / rg - icieq + icfeq + (1 / rf + gcf) * vout) / (1 / rg + gci + 1 / rf + gcf); // reduces to above when Cf, Ci = 0
        } else if (topology === Consts.TOPOLOGY.INVERTING) {
            result = ((vout - vin) * rg) / (rf + rg) + vin;
            // result = (vin / rg - icieq + icfeq + (1 / rf + gcf) * vout) / (1 / rg + gci + 1 / rf + gcf); // reduces to above when Cf, Ci = 0
        }

        return result;
    },
    calculateVout(vpole, vin, vcap, bias, rext, rg, rf, ro, gain, topology, gci = 0, gcf = 0, icieq = 0, icfeq = 0) {
        let result;

        const rgth = this.parallel(rg, 1 / gci);
        const rfth = this.parallel(rf, 1 / gcf);

        if (topology === Consts.TOPOLOGY.FOLLOWER || (topology === Consts.TOPOLOGY.NON_INVERTING && gain === 1)) {
            result = (vpole / ro + vin / (rf + rg) + vcap / rext) / (1 / ro + 1 / (rf + rg) + 1 / rext);
        } else if (topology === Consts.TOPOLOGY.NON_INVERTING) {
            // result = (vpole / ro + vcap / rext) / (1 / ro + 1 / rext); //why no bias in old equation, also missing 1/(rf + rg) conductance? keep consistent
            result =
                (vpole / ro + vcap / rext + ((bias / rg - icieq) * rgth - icfeq * rfth) / (rgth + rfth)) /
                (1 / ro + 1 / rext + 1 / (rgth + rfth)); // reduces to above when Cf, Ci = 0
        } else if (topology === Consts.TOPOLOGY.INVERTING) {
            // result = (vpole / ro + vin / (rf + rg) + vcap / rext) / (1 / ro + 1 / (rf + rg) + 1 / rext);
            result =
                (vpole / ro + vcap / rext + ((vin / rg - icieq) * rgth - icfeq * rfth) / (rgth + rfth)) /
                (1 / ro + 1 / rext + 1 / (rgth + rfth)); // reduces to above when Cf, Ci = 0
        }

        return result;
    },
    calculateVoltages(params, method = '') {
        //default method is cramer. The linearAlgebra method causes some unit test errors 'Matrix is near singular' for some sigma deltas
        const { vin, bias, rg, rf, ro, rext, gain, alpha, topology, gci, gcf, gcext, ieqci, ieqcf, ieqcext, veq } =
            params;
        let result;
        if (topology === Consts.TOPOLOGY.FOLLOWER || (topology === Consts.TOPOLOGY.NON_INVERTING && gain === 1)) {
            const g11 = gci + 1 / ro + 1 / rext;
            const g12 = -1 / ro;
            const g13 = -1 / rext;
            const g21 = alpha;
            const g22 = 1;
            const g31 = 1 / rext;
            const g33 = -(1 / rext + gcext);
            const b1 = -ieqci;
            const b2 = alpha * vin + veq;
            const b3 = ieqcext;

            if (method === 'linearAlgebra') {
                // use linalg.solve for better numerical stability, but results in slightly different values due to floating point precision issues
                const [vout, vpole, vcap] = linalg.solve(
                    [
                        [g11, g12, g13],
                        [g21, g22, 0],
                        [g31, 0, g33],
                    ],
                    [b1, b2, b3],
                );
                result = { vp: vin, vm: vout, vpole, vout, vcap };
            } else {
                // Cramer's rule for vcap = v3: numerator is determinant of matrix with b replacing column 3,
                // denominator is determinant of coefficient matrix
                const detG1 = g22 * g33 * b1 - g12 * g33 * b2 - g13 * g22 * b3;
                const detG2 = -g21 * g33 * b1 + (g11 * g33 - g13 * g31) * b2 + g13 * g21 * b3;
                const detG3 = -g22 * g31 * b1 + g12 * g31 * b2 + (g11 * g22 - g12 * g21) * b3;
                const detG = g11 * g22 * g33 - g12 * g21 * g33 - g13 * g22 * g31;
                // node order is [vout, vpole, vcap]
                const [vout, vpole, vcap] = [detG1, detG2, detG3].map((g) => g / detG);
                result = { vp: vin, vm: vout, vpole, vout, vcap };
            }
        } else {
            // NON_INVERTING and INVERTING topologies share the same system structure
            const isInverting = topology === Consts.TOPOLOGY.INVERTING;

            // Topology-specific coefficient values
            const g11 = isInverting ? -(gci + gcf + 1 / rf + 1 / rg) : 1 / rg + gci + gcf + 1 / rf;
            const g13 = isInverting ? gcf + 1 / rf : -(gcf + 1 / rf);
            const b1 = isInverting ? -vin / rg + ieqci - ieqcf : bias / rg - ieqci + ieqcf;
            const b2 = isInverting ? alpha * bias + veq : alpha * vin + veq;
            const vp = isInverting ? bias : vin;

            // Common coefficients for both topologies
            const g21 = alpha;
            const g22 = 1;
            const g31 = 1 / rf + gcf;
            const g32 = 1 / ro;
            const g33 = -(1 / ro + 1 / rext + 1 / rf + gcf);
            const g34 = 1 / rext;
            const g43 = 1 / rext;
            const g44 = -(1 / rext + gcext);
            const b3 = ieqcf;
            const b4 = ieqcext;

            if (method === 'linearAlgebra') {
                // use linalg.solve for better numerical stability, but results in slightly different values due to floating point precision issues
                const [vm, vpole, vout, vcap] = linalg.solve(
                    [
                        [g11, 0, g13, 0],
                        [g21, g22, 0, 0],
                        [g31, g32, g33, g34],
                        [0, 0, g43, g44],
                    ],
                    [b1, b2, b3, b4],
                );
                result = { vp, vm, vpole, vout, vcap };
            } else {
                // Cramer's rule for vcap = v4: numerator is determinant of matrix with b replacing column 4,
                // denominator is determinant of coefficient matrix
                const detG1 =
                    (g22 * g33 * g44 - g22 * g34 * g43) * b1 +
                    g13 * g32 * g44 * b2 +
                    -g13 * g22 * g44 * b3 +
                    g13 * g22 * g34 * b4;
                const detG2 =
                    (g21 * g34 * g43 - g21 * g33 * g44) * b1 +
                    (g11 * g33 * g44 - g11 * g34 * g43 - g13 * g31 * g44) * b2 +
                    g13 * g21 * g44 * b3 +
                    -g13 * g21 * g34 * b4;
                const detG3 =
                    (g21 * g32 * g44 - g22 * g31 * g44) * b1 +
                    -g11 * g32 * g44 * b2 +
                    g11 * g22 * g44 * b3 +
                    -g11 * g22 * g34 * b4;
                const detG4 =
                    (g22 * g31 * g43 - g21 * g32 * g43) * b1 +
                    g11 * g32 * g43 * b2 +
                    -g11 * g22 * g43 * b3 +
                    (g11 * g22 * g33 + g13 * g21 * g32 - g13 * g22 * g31) * b4;
                const detG =
                    g11 * g22 * g33 * g44 - g11 * g22 * g34 * g43 + g13 * g21 * g32 * g44 - g13 * g22 * g31 * g44;
                // node order is [vm, vpole, vout, vcap]
                const [vm, vpole, vout, vcap] = [detG1, detG2, detG3, detG4].map((g) => g / detG);
                result = { vp, vm, vpole, vout, vcap };
            }
        }
        return result;
    },
    calculateKickback(vcap, vcapPrevious, vref, kickbackType, kickbackVrefMultiplier, cext, cadc) {
        let result = {
            kickback: 0,
            minKickbackDominates: false,
            kickbackMag: 0,
        };

        if (kickbackType === Consts.KICKBACK_TYPE.PREV_SAMPLE) {
            let kickback = (cadc * vcapPrevious + cext * vcap) / (cext + cadc);
            let minKickback = (cadc * (vcap - vref * kickbackVrefMultiplier) + cext * vcap) / (cext + cadc);

            if (minKickback < kickback) result.minKickbackDominates = true;

            result.kickback = Math.min(kickback, minKickback);
        } else if (kickbackType === Consts.KICKBACK_TYPE.FIXED) {
            result.kickback = (cadc * vref * kickbackVrefMultiplier + cext * vcap) / (cext + cadc);
        }

        result.kickbackMag = vcap - result.kickback;

        return result;
    },
    calculateIdealVin(vin, bias, gain, topology) {
        let result;

        if (topology === Consts.TOPOLOGY.FOLLOWER || topology === Consts.TOPOLOGY.NON_INVERTING) {
            result = (vin - bias) * gain + bias;
        } else if (topology === Consts.TOPOLOGY.INVERTING) {
            result = (vin - bias) * -gain + bias;
        }

        return result;
    },
    calculateLsbError(vcap, vref, bits, voutFinal) {
        return (-(voutFinal - vcap) * Math.pow(2, bits)) / vref;
    },
    calculateVoltsError(vcap, voutFinal) {
        return -(voutFinal - vcap);
    },
    convertVoltsToLsb(volts, vref, bits) {
        return (volts * Math.pow(2, bits)) / vref;
    },
    calculateRequiredBw(vcapStep, vref, bits, tacq) {
        let halfLSB = vref / Math.pow(2, bits + 1);
        let tc = Math.log(Math.abs(vcapStep / halfLSB));
        let tau = tacq / tc;
        let requiredBw = 1 / (2 * Math.PI * tau);

        return requiredBw;
    },
    //#endregion inputSettlingCalculations

    //#region noiseCalculations

    getNoiseCalculationItem(density, sourceInputDensity, sourceNoiseGain, totalVrms, totalPerc, totalSnr, bw = null) {
        return {
            density: density,
            sourceInputDensity: sourceInputDensity,
            sourceNoiseGain: sourceNoiseGain,
            totalVrms: totalVrms,
            totalPerc: totalPerc,
            totalSnr: totalSnr,
            bw: bw,
        };
    },

    // will probably need to pull out halfNyquistZones + partialNyquistZone into it's own computed property in future noise ticket
    calculateDriverAndSourceEquivBw(data) {
        let effectiveFs = data.fmod * 2;
        let driverAndSourceEquivNoiseBW = Math.min(data.driverEquivBW, data.rfiltEquivBW),
            highestFullNyquistZone = Math.floor((driverAndSourceEquivNoiseBW - data.adcEquivBW) / effectiveFs),
            halfNyquistZones = highestFullNyquistZone + 2 + 1,
            partialNyquistZoneInitial =
                data.adcEquivBW - ((highestFullNyquistZone + 1) * effectiveFs - driverAndSourceEquivNoiseBW),
            partialNyquistZone = partialNyquistZoneInitial < 0 ? 0 : partialNyquistZoneInitial;

        return halfNyquistZones * data.adcEquivBW + partialNyquistZone;
    },

    calculateNoiseRMS(noiseDensity, noiseBW) {
        return noiseDensity * Math.sqrt(noiseBW);
    },

    calculateEqNoiseBW(noiseBW) {
        return noiseBW * (Math.PI / 2);
    },

    calculateRSS(contributions) {
        let i = 0,
            sumSquares = 0;
        for (i = 0; i < contributions.length; i++) {
            sumSquares = sumSquares + Math.pow(contributions[i], 2);
        }
        return Math.sqrt(sumSquares);
    },

    calculateNoisePercent(contribution, total) {
        let ratio = Math.pow(contribution, 2) / Math.pow(total, 2);
        let percent = Math.round(ratio * 100);
        return percent;
    },

    // Second Order Noise Transfer functions
    calculateAmplifierDynamics(frequency, rf, rg, cf, ci, gbw) {
        const s = Complex.createComplex(0, 2 * Math.PI * frequency); // s = jω

        // Compute the terms in the denominator
        const term1 = s.mult(s).mult(Complex.createComplex((rf * (cf + ci)) / (2 * Math.PI * gbw), 0)); // rf * (cf + ci) * s^2 / (2 * pi * gbw)
        const term2 = s.mult(Complex.createComplex((rf + rg) / (2 * Math.PI * gbw * rg) + rf * cf, 0)); // ((rf + rg) / (2 * pi * gbw * rg) + rf * cf) * s
        const term3 = Complex.createComplex(1, 0); // Constant term: 1

        // Combine the terms to form the denominator
        const denominator = term1.plus(term2).plus(term3);

        // Compute the dynamics: 1 / denominator
        const dynamics = Complex.createComplex(1, 0).divide(denominator);

        return dynamics;
    },

    calculateExtFilterGain(frequency, rfilt, cfilt) {
        // GRfilt = 1 / (1 + s * RFILT * CFILT)
        const s = Complex.createComplex(0, 2 * Math.PI * frequency);
        const numerator = Complex.createComplex(1, 0); // 1
        const denominator = Complex.createComplex(1, 0).plus(s.mult(Complex.createComplex(rfilt * cfilt, 0)));
        return numerator.divide(denominator);
    },

    calculateAmplifierGain(frequency, rf, rg, cf, ci, gbw, rfilt, cfilt) {
        // GAmp = ((rf + rg) / rg + rf * (cf + ci) * s ) * GRfilt * Gmain
        const s = Complex.createComplex(0, 2 * Math.PI * frequency);
        // const dcGain = (rf + rg) / rg; // DC Gain
        // Normalize dcGain to 1; multiplier calculated based on noiseConfig
        const numerator = Complex.createComplex(1, 0).plus(
            s.mult(Complex.createComplex(this.parallel(rg, rf) * (cf + ci), 0)),
        );
        return numerator
            .mult(this.calculateExtFilterGain(frequency, rfilt, cfilt))
            .mult(this.calculateAmplifierDynamics(frequency, rf, rg, cf, ci, gbw));
    },

    calculateRgGain(frequency, rf, rg, cf, ci, gbw, rfilt, cfilt) {
        // GRG = -(rf / rg) * GRfilt * Gmain
        // const dcGain = -rf / rg; // DC Gain
        // Normalize dcGain to 1; multiplier calculated based on noiseConfig
        const numerator = Complex.createComplex(1, 0);
        return numerator
            .mult(this.calculateExtFilterGain(frequency, rfilt, cfilt))
            .mult(this.calculateAmplifierDynamics(frequency, rf, rg, cf, ci, gbw));
    },

    calculateRfGain(frequency, rf, rg, cf, ci, gbw, rfilt, cfilt) {
        // GRF = GRfilt * Gmain
        return this.calculateExtFilterGain(frequency, rfilt, cfilt).mult(
            this.calculateAmplifierDynamics(frequency, rf, rg, cf, ci, gbw),
        );
    },

    calculateIbGain(frequency, rf, rg, cf, ci, gbw, rfilt, cfilt) {
        // Gib = rf * GRfilt * Gmain
        // const dcGain = rf; // DC Gain
        // Normalize dcGain to 1; multiplier calculated based on noiseConfig
        const numerator = Complex.createComplex(this.parallel(rf, rg), 0);
        return numerator
            .mult(this.calculateExtFilterGain(frequency, rfilt, cfilt))
            .mult(this.calculateAmplifierDynamics(frequency, rf, rg, cf, ci, gbw));
    },

    calculateNoiseRMSbyIntegration(frequencies, noiseDensityCurve) {
        let rmsNoise = 0;

        for (let i = 1; i < frequencies.length; i++) {
            let frequencySpan = frequencies[i] - frequencies[i - 1];
            let noiseAverage = (noiseDensityCurve[i] + noiseDensityCurve[i - 1]) / 2; //var noiseAverage = Math.sqrt(noiseDensityCurve[i] * noiseDensityCurve[i - 1]); // look into this method of calculating noise average.
            let noiseRootHz = noiseAverage * Math.sqrt(frequencySpan);
            rmsNoise = Math.sqrt(rmsNoise ** 2 + noiseRootHz ** 2);
        }

        return rmsNoise;
    },
    //#endregion noiseCalculations
};
